// RSI and divergence on the chart (DWLF-338). The RSI pane and its exit / midline events use the
// jobs' RSI(14), computed over the FULL series and clipped to the visible window, so a short window
// (mobile) keeps the warm-up. Divergences are the jobs' STORED events (they need the confirmed cycle
// pivots, which only the server has): a marker on the bar the later pivot was confirmed, and a line
// joining the two pivots on price and on the oscillator, so the claim can be checked by eye.
import { CycleDivergence, RSI as LibraryRSI } from '@andywilliams/indicators';
import { DIVERGENCE_EVENT_DEFINITIONS } from '../../utils/divergenceEvents';

// The jobs' length and the library's default levels, read rather than copied.
export const RSI_PARAMS = Object.freeze({ length: CycleDivergence.RSI_LENGTH });
const { overbought, midline, oversold } = LibraryRSI.resolveRsiParams(RSI_PARAMS);
export const RSI_GUIDE_LEVELS = Object.freeze({ overbought, midline, oversold });

const inWindow = (t, { from, to }) => (from === undefined || t >= from) && (to === undefined || t <= to);

/** RSI as [{ t, v }] within the window, or null when there is nothing to draw. */
export const rsiSeries = (RSI, { fullCandles, from, to }) => {
  const points = (RSI.computeRsi(fullCandles, RSI_PARAMS).rsi || [])
    .filter((point) => point && Number.isFinite(point.t) && Number.isFinite(point.v) && inWindow(point.t, { from, to }))
    .map((point) => ({ t: point.t, v: point.v }));
  return points.length ? points : null;
};

/** The jobs' rsi.exit.* and rsi.midline.* events (same detector and length) within the window. */
export const rsiEvents = (RSI, { fullCandles, from, to }) => (
  RSI.detectEvents(fullCandles, RSI_PARAMS).filter((event) => inWindow(event.t, { from, to }))
);

export const rsiPaneTitle = () => `RSI (${RSI_PARAMS.length})`;

const DEFINITIONS = new Map(DIVERGENCE_EVENT_DEFINITIONS.map((definition) => [definition.id, definition]));

export const DIVERGENCE_EVENT_TYPES = Object.freeze([...DEFINITIONS.keys()]);

/** The oscillator a divergence id is about: 'rsi' or 'dss'. */
export const divergenceOscillator = (id) => (typeof id === 'string' ? id.split('.')[0] : null);

/**
 * /v2/events row → the library event shape the chart's markers take, stamped at the confirm bar
 * (eventTimestamp), with both pivots in the payload. Null for anything that is not a divergence.
 */
export const toDivergenceLibraryEvent = (stored) => {
  if (!stored || !DEFINITIONS.has(stored.eventType)) {return null;}
  const t = Date.parse(stored.eventTimestamp ?? stored.date);
  const d = stored.divergence || {};
  const pivotTime = Number(stored.pivotTime);
  if (!Number.isFinite(t) || !Number.isFinite(pivotTime)) {return null;}
  const definition = DEFINITIONS.get(stored.eventType);
  return {
    id: stored.eventType,
    name: definition.name,
    description: definition.description,
    t,
    payload: {
      kind: d.kind,
      direction: d.direction,
      pivotTime,
      pivotPrice: Number(d.pivotPrice),
      oscillatorValue: Number(d.oscillatorValue),
      previousPivotTime: Number(d.previousPivotTime),
      previousPivotPrice: Number(d.previousPivotPrice),
      previousOscillatorValue: Number(d.previousOscillatorValue),
      source: d.source || {},
    },
  };
};

/** Whether a DSS divergence was read off a DSS with these settings (so its line sits on the drawn curve). */
export const dssSourceMatches = (event, params) => {
  const source = event?.payload?.source || {};
  return ['length', 'smooth1', 'signal'].every((key) => Number(source[key]) === Number(params?.[key]));
};

export const DIVERGENCE_COLORS = Object.freeze({ bullish: '#66BB6A', bearish: '#EF5350' });

/** bullish / bearish from a divergence id. */
export const divergenceDirection = (id) => (/bearish$/.test(id) ? 'bearish' : 'bullish');

/**
 * One two-point line per divergence of `oscillator` whose pivots are both inside [from, to]:
 * pivot to pivot on price (`on: 'price'`) or on the oscillator (`on: 'oscillator'`). Regular
 * divergences are solid, hidden ones dashed.
 */
export const divergenceSegments = (events, { oscillator, on, from, to }) => (events || [])
  .filter((event) => divergenceOscillator(event?.id) === oscillator)
  .map((event) => {
    const p = event.payload || {};
    const start = on === 'price' ? p.previousPivotPrice : p.previousOscillatorValue;
    const end = on === 'price' ? p.pivotPrice : p.oscillatorValue;
    if (![p.previousPivotTime, p.pivotTime, start, end].every(Number.isFinite)) {return null;}
    if (!inWindow(p.previousPivotTime, { from, to }) || !inWindow(p.pivotTime, { from, to })) {return null;}
    const direction = divergenceDirection(event.id);
    return {
      key: `${event.id} ${on} ${p.pivotTime}`,
      type: 'line',
      data: [{ t: p.previousPivotTime, v: start }, { t: p.pivotTime, v: end }],
      style: { color: DIVERGENCE_COLORS[direction], lineWidth: 1.6, dashed: /\.hidden\./.test(event.id) },
      showInTooltip: false,
    };
  })
  .filter(Boolean);
